import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Spade, Heart, Diamond, Club, Activity, Users, Timer, Layers } from "lucide-react";
import { Link } from "react-router-dom";

const fmtDuration = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const StatChip = ({ value, label, suit, accent }) => {
  const SuitIcon = { spade: Spade, heart: Heart, diamond: Diamond, club: Club }[suit] || Spade;
  return (
    <div className={`panel p-6 card-suit deal ${accent || ""}`} data-suit={suit.toUpperCase()}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mono">{label}</div>
          <div className="font-serif text-4xl mt-3 tracking-tight">{value}</div>
        </div>
        <SuitIcon size={18} className={suit === "heart" || suit === "diamond" ? "text-red-500" : "text-neutral-400"} />
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/dashboard/summary")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  return (
    <div className="p-8 lg:p-12">
      <header className="mb-10">
        <div className="label-mono text-green-400 mb-2">// table.overview</div>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="dashboard-title">
          The Table
        </h1>
        <p className="text-neutral-400 text-sm mt-2 max-w-xl">
          Live snapshot of every project, task and timed session you have on the felt.
        </p>
      </header>

      {err && <div className="panel p-4 border-red-700 text-red-300 text-xs">{err}</div>}

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="dashboard-stats">
        <StatChip value={data?.projects ?? "—"} label="Active Projects" suit="spade" accent="panel-accent-red" />
        <StatChip value={data?.tasks_in_progress ?? "—"} label="Tasks In Play" suit="heart" accent="panel-accent-green" />
        <StatChip value={data?.tasks_done ?? "—"} label="Tasks Closed" suit="diamond" accent="panel-accent-gold" />
        <StatChip value={data ? fmtDuration(data.time_seconds) : "—"} label="Time Logged" suit="club" />
      </section>

      <section className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 panel p-6 deal">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-mono text-amber-400">{"//"} recent.hands</div>
              <h2 className="font-serif text-2xl mt-1">Recent Tasks</h2>
            </div>
            <Link to="/app/projects" data-testid="dashboard-view-projects" className="btn-ghost">View Projects</Link>
          </div>
          <div className="divide-y divide-neutral-900">
            {(data?.recent_tasks || []).length === 0 && (
              <div className="py-10 text-center text-neutral-500 font-jet text-xs">
                No tasks yet. Create a project and deal your first hand.
              </div>
            )}
            {(data?.recent_tasks || []).map((t) => (
              <div key={t.id} className="py-3 flex items-center gap-4" data-testid={`recent-task-${t.id}`}>
                <div className={`w-2 h-8 ${t.status === "done" ? "bg-green-500" : t.status === "in_progress" ? "bg-amber-400" : "bg-neutral-700"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.title}</div>
                  <div className="label-mono mt-1">{t.status} · {t.priority}</div>
                </div>
                <div className="label-mono text-neutral-500 hidden sm:block">
                  {t.updated_at?.slice(0, 10)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel p-6 deal" style={{ animationDelay: "120ms" }}>
          <div className="label-mono text-green-400">{"//"} house.stats</div>
          <h2 className="font-serif text-2xl mt-1 mb-5">Holdings</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3"><Layers size={14} className="text-neutral-500" /><span className="flex-1">Total Tasks</span><span className="font-jet">{data?.tasks_total ?? "—"}</span></li>
            <li className="flex items-center gap-3"><Activity size={14} className="text-amber-400" /><span className="flex-1">To Do</span><span className="font-jet">{data?.tasks_todo ?? "—"}</span></li>
            <li className="flex items-center gap-3"><Users size={14} className="text-neutral-500" /><span className="flex-1">Team Members</span><span className="font-jet">{data?.members ?? "—"}</span></li>
            <li className="flex items-center gap-3"><Timer size={14} className="text-green-400" /><span className="flex-1">Time Tracked</span><span className="font-jet">{data ? fmtDuration(data.time_seconds) : "—"}</span></li>
          </ul>
          <div className="divider-dotted my-6" />
          <div className="label-mono">$ system_status</div>
          <pre className="text-xs text-green-400 mt-2 font-jet">
{`> auth.....OK
> mongo....OK
> ai.suite.READY`}
          </pre>
        </div>
      </section>
    </div>
  );
}
